import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { Upload, Loader2, FileSpreadsheet, ArrowRight, CheckCircle2, Users, RefreshCcw, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELD_LABELS = {
  full_name: 'Full name', first_name: 'First name', last_name: 'Last name',
  email: 'Email', phone: 'Phone', job_title: 'Job title',
  linkedin_url: 'LinkedIn', company_name: 'Company name', company_domain: 'Company domain',
  name: 'Company name', domain: 'Domain', website: 'Website', industry: 'Industry',
  location: 'Location', description: 'Description', employee_count: 'Employees',
};

const NONE = '__none__';

const StatCard = ({ value, label, color }) => (
  <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 text-center">
    <div className={`text-2xl font-bold ${color}`}>{value.toLocaleString()}</div>
    <div className="text-xs text-gray-500 mt-0.5">{label}</div>
  </div>
);

const ImportDialog = ({ objectType, open, onOpenChange, onDone }) => {
  const [step, setStep] = useState('upload'); // upload | map | review | done
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [dupMode, setDupMode] = useState('update'); // update | skip | create
  const [matchStats, setMatchStats] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => {
    setStep('upload'); setFile(null); setPreview(null); setMapping({});
    setDupMode('update'); setMatchStats(null); setResult(null);
  };

  const close = (o) => { if (!o) reset(); onOpenChange(o); };

  const cleanMapping = () => {
    const clean = {};
    Object.entries(mapping).forEach(([k, v]) => { if (v && v !== NONE) clean[k] = v; });
    return clean;
  };

  const doPreview = async () => {
    if (!file) { toast.error('Choose a file'); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('object_type', objectType);
      fd.append('file', file);
      const res = await axios.post(`${API}/import/preview`, fd);
      setPreview(res.data);
      const m = {};
      Object.entries(res.data.suggested_mapping || {}).forEach(([k, v]) => { m[k] = v; });
      setMapping(m);
      setStep('map');
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not read file'); }
    finally { setLoading(false); }
  };

  const goReview = async () => {
    const clean = cleanMapping();
    if (Object.keys(clean).length === 0) { toast.error('Map at least one column'); return; }
    setStep('review');
    setMatchStats(null);
    setAnalyzing(true);
    try {
      const fd = new FormData();
      fd.append('object_type', objectType);
      fd.append('mapping', JSON.stringify(clean));
      fd.append('file', file);
      const res = await axios.post(`${API}/import/match-preview`, fd);
      setMatchStats(res.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not analyse matches'); }
    finally { setAnalyzing(false); }
  };

  const doCommit = async () => {
    const clean = cleanMapping();
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('object_type', objectType);
      fd.append('mapping', JSON.stringify(clean));
      fd.append('file', file);
      fd.append('duplicate_handling', dupMode);
      const res = await axios.post(`${API}/import/commit`, fd);
      setResult(res.data);
      setStep('done');
      if (onDone) onDone();
    } catch (err) { toast.error(err.response?.data?.detail || 'Import failed'); }
    finally { setLoading(false); }
  };

  const headers = preview?.headers || [];
  const standardFields = preview?.standard_fields || [];
  const customFields = preview?.custom_fields || [];
  const label = objectType === 'contact' ? 'Contacts' : 'Companies';
  const matchNoun = objectType === 'contact' ? 'same email' : 'same domain or name';

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="import-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl text-gray-900 flex items-center gap-2">
            <Upload className="w-5 h-5 text-violet-600" /> Import {label}
          </DialogTitle>
          <DialogDescription className="text-gray-500">
            Upload a CSV or XLSX, map your columns, then review how many rows are new vs. matches before importing.
          </DialogDescription>
        </DialogHeader>

        {step === 'upload' && (
          <div className="space-y-4 mt-2">
            <label className="block border-2 border-dashed border-gray-300 rounded-xl p-8 text-center cursor-pointer hover:border-violet-400 hover:bg-violet-50/40 transition-colors" data-testid="import-dropzone">
              <FileSpreadsheet className="w-8 h-8 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-600">{file ? file.name : 'Click to choose a .csv or .xlsx file'}</p>
              <input type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={e => setFile(e.target.files[0])} data-testid="import-file-input" />
            </label>
            <DialogFooter>
              <Button variant="outline" onClick={() => close(false)}>Cancel</Button>
              <Button className="bg-violet-600 hover:bg-violet-700 text-white" onClick={doPreview} disabled={!file || loading} data-testid="import-preview-btn">
                {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Reading…</> : <>Next <ArrowRight className="w-4 h-4 ml-2" /></>}
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === 'map' && preview && (
          <div className="space-y-3 mt-2">
            <p className="text-xs text-gray-500">{preview.total_rows} rows found. Match your columns to fields:</p>
            <div className="space-y-2 max-h-[45vh] overflow-y-auto pr-1">
              {standardFields.map(f => (
                <div key={f} className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-center">
                  <Label className="text-gray-700 text-sm">{FIELD_LABELS[f] || f}</Label>
                  <Select value={mapping[f] || NONE} onValueChange={v => setMapping(m => ({ ...m, [f]: v }))}>
                    <SelectTrigger className="h-9" data-testid={`map-${f}`}><SelectValue placeholder="-" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>- skip -</SelectItem>
                      {headers.map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ))}
              {customFields.length > 0 && (
                <>
                  <p className="text-[11px] uppercase tracking-wider text-gray-400 pt-2">Custom properties</p>
                  {customFields.map(cf => (
                    <div key={cf.name} className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-center">
                      <Label className="text-gray-700 text-sm">{cf.label}</Label>
                      <Select value={mapping[`custom:${cf.name}`] || NONE} onValueChange={v => setMapping(m => ({ ...m, [`custom:${cf.name}`]: v }))}>
                        <SelectTrigger className="h-9" data-testid={`map-custom-${cf.name}`}><SelectValue placeholder="-" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value={NONE}>- skip -</SelectItem>
                          {headers.map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </>
              )}
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setStep('upload')}>Back</Button>
              <Button className="bg-violet-600 hover:bg-violet-700 text-white" onClick={goReview} data-testid="import-review-btn">
                Next: check for matches <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === 'review' && (
          <div className="space-y-4 mt-2" data-testid="import-review">
            <p className="text-sm text-gray-600">We checked your file against your existing {label.toLowerCase()} ({matchNoun}):</p>
            {analyzing ? (
              <div className="flex items-center justify-center gap-2 py-8 text-gray-500" data-testid="import-analyzing">
                <Loader2 className="w-5 h-5 animate-spin text-violet-600" /> Analysing matches…
              </div>
            ) : matchStats ? (
              <>
                <div className="grid grid-cols-3 gap-3" data-testid="match-stats">
                  <StatCard value={matchStats.new} label="New records" color="text-emerald-600" />
                  <StatCard value={matchStats.matches_existing} label="Match existing" color="text-amber-600" />
                  <StatCard value={matchStats.invalid} label="Invalid / empty" color="text-gray-400" />
                </div>

                {matchStats.matches_existing > 0 && (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 space-y-2">
                    <div className="flex items-center gap-2 text-amber-800 text-sm font-medium">
                      <AlertTriangle className="w-4 h-4" />
                      {matchStats.matches_existing.toLocaleString()} {matchStats.matches_existing === 1 ? 'row matches' : 'rows match'} an existing record
                    </div>
                    <Label className="text-gray-700 text-sm">How should we handle them?</Label>
                    <Select value={dupMode} onValueChange={setDupMode}>
                      <SelectTrigger className="h-9 bg-white" data-testid="dup-handling-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="update">Update the existing record (merge)</SelectItem>
                        <SelectItem value="skip">Skip the row (keep existing as-is)</SelectItem>
                        <SelectItem value="create">Create a new record anyway</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <button onClick={goReview} className="text-xs text-violet-600 hover:text-violet-700 inline-flex items-center gap-1" data-testid="reanalyze-btn">
                  <RefreshCcw className="w-3 h-3" /> Re-check
                </button>
              </>
            ) : (
              <div className="text-sm text-gray-500 py-4">Couldn't analyse - you can still import.</div>
            )}

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setStep('map')}>Back</Button>
              <Button className="bg-violet-600 hover:bg-violet-700 text-white" onClick={doCommit} disabled={loading || analyzing} data-testid="import-commit-btn">
                {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Importing…</> : (
                  matchStats ? `Import ${(matchStats.new + (dupMode === 'skip' ? 0 : matchStats.matches_existing)).toLocaleString()} ${label.toLowerCase()}` : 'Import'
                )}
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === 'done' && result && (
          <div className="space-y-4 mt-2 text-center py-4" data-testid="import-result">
            <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto" />
            <div>
              <p className="text-lg text-gray-900 font-medium">Import complete</p>
              <p className="text-sm text-gray-500 mt-1">
                <span className="text-emerald-600 font-medium">{result.created} created</span> ·{' '}
                <span className="text-violet-600 font-medium">{result.updated} updated</span> ·{' '}
                <span className="text-amber-600 font-medium">{result.duplicates_skipped || 0} duplicates skipped</span> ·{' '}
                <span className="text-gray-400">{result.skipped} invalid</span>
              </p>
            </div>
            <DialogFooter className="justify-center sm:justify-center">
              <Button className="bg-violet-600 hover:bg-violet-700 text-white" onClick={() => close(false)} data-testid="import-done-btn">
                <Users className="w-4 h-4 mr-2" /> Done
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ImportDialog;
